import { Box, Dialog, DialogContent } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { FilterDate } from "../../../types/commonForm";
import { Coupon, CouponTypes } from "../../../types/coupons";
import { CouponCreateModal } from "../../common/couponsShared/couponsModals/couponCreateModal";
import { CouponViewModal } from "../../common/couponsShared/couponsModals/couponModalView";
import { CouponsTable } from "../../common/couponsShared/couponsTable/couponsTable";
import { AppButton } from "../../common/ctaButton/button";
import { RemoveEntityModalContent } from "../../common/removeEntityPopUp/RemoveEntity";

interface State {
  coupons: Array<Coupon>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
  typeFilter: Array<string>;
  statusFilter: Array<string>;
  startDate: FilterDate;
  endDate: FilterDate;
}

export const SingleCouponsTab = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [state, setState] = useState<State>({
    coupons: Array<Coupon>(),
    total: 0,
    page: 0,
    perPage: 10,
    field: "_id",
    order: "desc",
    searchField: "couponId",
    searchValue: "",
    typeFilter: Array<string>(),
    statusFilter: Array<string>(),
    startDate: {
      startDate: undefined,
      endDate: undefined,
    },
    endDate: {
      startDate: undefined,
      endDate: undefined,
    },
  });

  const [currentCoupon, setCurrentCoupon] = useState<Coupon>();
  const [modalsState, setModals] = useState({
    viewCoupon: false,
    createCoupon: false,
    editCoupon: false,
    deleteCoupon: false,
  });

  const [typeFilter, setTypeFilter] = useState({
    [CouponTypes.Basket]: false,
    [CouponTypes.Credit]: false,
    [CouponTypes.Prize]: false,
    [CouponTypes.FixedOdds]: false,
    [CouponTypes.DreamHome]: false,
  });

  const [statusFilter, setStatusFilter] = useState({
    Active: false,
    Inactive: false,
    Used: false,
    Pending: false,
  });

  useEffect(() => {
    getCoupons(
      1,
      10,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      Array<string>(),
      Array<string>(),
      state.startDate,
      state.endDate
    );
  }, []);
  const getCoupons = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchField: string,
    searchValue: string,
    typeFilter: Array<string>,
    statusFilter: Array<string>,
    startDate: FilterDate,
    endDate: FilterDate
  ): Promise<void> => {
    const resource = "/coupons/get";
    const result = (await dataProvider.getCoupons(resource, {
      pagination: { page, perPage },
      sort: { field, order },
      search: { field: searchField, value: searchValue },
      filter: {
        typeFilter: typeFilter,
        statusFilter: statusFilter,
        startDate: startDate,
        endDate: endDate,
      },
    })) as any;
    setState({
      ...state,
      coupons: result.data.coupons,
      total: result.data.allCount,
      page,
      perPage,
      field,
      order,
      searchField,
      searchValue,
      statusFilter,
      typeFilter,
    });
  };

  const handleFirstPage = () => {
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getCoupons(
      page,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getCoupons(
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getCoupons(
      1,
      perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangeCouponStatus = async (couponId: string | undefined) => {
    const coupon = state.coupons.find((i) => i._id === couponId);
    const couponIndex = state.coupons.findIndex((i) => i._id === couponId);

    if (coupon) {
      try {
        const res = (await dataProvider.changeCouponStatus("/coupons/status", {
          _id: state.coupons[couponIndex]._id,
          isActive: !state.coupons[couponIndex].isActive,
        })) as any;
        if (res && res.data === 200) {
          setState({
            ...state,
            coupons: [
              ...state.coupons.slice(0, couponIndex),
              {
                ...state.coupons[couponIndex],
                isActive: !state.coupons[couponIndex].isActive,
              },
              ...state.coupons.slice(couponIndex + 1),
            ],
          });

          getCoupons(
            state.page,
            state.perPage,
            state.field,
            state.order,
            state.searchField,
            state.searchValue,
            state.typeFilter,
            state.statusFilter,
            state.startDate,
            state.endDate
          );
        }
      } catch (err) {
        notify(`${err} `, "error");
      }
    }
  };

  const handleCloseModals =
    (
      modalName: "viewCoupon" | "editCoupon" | "createCoupon" | "deleteCoupon"
    ) =>
    () => {
      setModals((state) => ({
        ...state,
        [modalName]: false,
      }));
    };
  const handleShowModals = (
    modalName: "viewCoupon" | "editCoupon" | "createCoupon" | "deleteCoupon"
  ): (() => void) => {
    if (
      modalName === "viewCoupon" ||
      modalName === "editCoupon" ||
      modalName === "deleteCoupon"
    ) {
      return (couponId?: string) => {
        const coupon = state.coupons.find((i) => i._id === couponId);
        setCurrentCoupon(coupon);
        setModals((state) => ({
          ...state,
          [modalName]: true,
        }));
      };
    }
    return () => {
      setModals((state) => ({
        ...state,
        [modalName]: true,
      }));
    };
  };

  const handleDeleteCoupon = (couponId: string) => async () => {
    try {
      const result = await dataProvider.delete("coupons", {
        id: couponId,
        previousData: {} as any,
      });
      if (result.data) {
        notify(result.data.message, "info");
        getCoupons(
          state.page,
          state.perPage,
          state.field,
          state.order,
          state.searchField,
          state.searchValue,
          state.typeFilter,
          state.statusFilter,
          state.startDate,
          state.endDate
        );
        setModals({
          ...modalsState,
          deleteCoupon: false,
        });
      }
    } catch (error) {
      notify("Coupon not found", "error");
    }
  };

  const handleSort = (direction: string, field: string) => {
    getCoupons(
      state.page,
      state.perPage,
      field,
      direction,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const applySearch = (field: string) => {
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      field,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const checkTypesFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const filter = typeFilter;
    filter[name] = event.target.checked;
    setState({
      ...state,
      typeFilter: Object.keys(filter).filter((status) => filter[status]),
    });
    setTypeFilter({ ...typeFilter, [name]: event.target.checked });
  };

  const checkStatusFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const filter = statusFilter;
    filter[name] = event.target.checked;
    setState({
      ...state,
      statusFilter: Object.keys(filter).filter((status) => filter[status]),
    });
    setStatusFilter({ ...statusFilter, [name]: event.target.checked });
  };

  const applyStatusFilter = () => {
    const filter = Object.keys(statusFilter).filter(
      (status) => statusFilter[status]
    );
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      filter,
      state.startDate,
      state.endDate
    );
  };

  const applyTypeFilter = () => {
    const filter = Object.keys(typeFilter).filter((type) => typeFilter[type]);
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      filter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const clearTypeFilter = () => {
    setTypeFilter({
      ...typeFilter,
      BASKET: false,
      CREDIT: false,
      PRIZE: false,
      FIXED_ODDS: false,
      DREAMHOME: false,
    });
    setState({ ...state, typeFilter: Array<string>() });
  };

  const clearStatusFilter = () => {
    setStatusFilter({
      ...statusFilter,
      Active: false,
      Inactive: false,
      Used: false,
    });
    setState({ ...state, statusFilter: Array<string>() });
  };

  const setStartStartDate = (value: Date) => {
    const startDateObject = state.startDate;
    startDateObject.startDate = value;
    setState({ ...state, startDate: startDateObject });
  };

  const setStartEndDate = (value: Date) => {
    const startDate = state.startDate;
    startDate.endDate = value;
    setState({ ...state, startDate: startDate });
  };

  const clearStartDateFilter = () => {
    const startDateObject = { startDate: undefined, endDate: undefined };
    setState({ ...state, startDate: startDateObject });
  };

  const clearEndDateFilter = () => {
    const endDateObject = { startDate: undefined, endDate: undefined };
    setState({ ...state, endDate: endDateObject });
  };

  const setEndStartDate = (value: Date) => {
    const endDateObject = state.endDate;
    endDateObject.startDate = value;
    setState({ ...state, endDate: endDateObject });
  };

  const setEndEndDate = (value: Date) => {
    const endDateObject = state.endDate;
    endDateObject.endDate = value;
    setState({ ...state, endDate: endDateObject });
  };

  const applyDateFilter = () => {
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const search = (value: string, field: string) => {
    setState({ ...state, searchField: field, searchValue: value });
  };

  const clearSearch = (field: string) => {
    setState({ ...state, searchValue: "" });
  };

  return (
    <Box className="couponsTabWrapper">
      <div className="addCupponRow">
        <AppButton
          onClick={handleShowModals("createCoupon")}
          text="Add Coupon"
        />
      </div>
      <CouponsTable
        coupons={state.coupons}
        tableComponentScope={"general"}
        page={state.page}
        perPage={state.perPage}
        collectionLength={state.coupons.length}
        total={state.total}
        isView={false}
        handleShowViewModal={handleShowModals("viewCoupon")}
        handleShowEditModal={handleShowModals("editCoupon")}
        handleShowDeleteModal={handleShowModals("deleteCoupon")}
        handleChangeCouponStatus={handleChangeCouponStatus}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleFirstPage={handleFirstPage}
        handleLastPage={handleLastPage}
        handleSort={handleSort}
        applySearch={applySearch}
        typeFilterValues={typeFilter}
        statusFilterValues={statusFilter}
        checkTypesFilter={checkTypesFilter}
        checkStatusFilter={checkStatusFilter}
        applyTypeFilter={applyTypeFilter}
        applyStatusFilter={applyStatusFilter}
        clearTypeFilter={clearTypeFilter}
        clearStatusFilter={clearStatusFilter}
        startDate={state.startDate}
        finishDate={state.endDate}
        setStartStartDate={setStartStartDate}
        setStartEndDate={setStartEndDate}
        clearStartDateFilter={clearStartDateFilter}
        applyDateFilter={applyDateFilter}
        setEndStartDate={setEndStartDate}
        setEndEndDate={setEndEndDate}
        clearEndDateFilter={clearEndDateFilter}
        search={search}
        clearSearch={clearSearch}
        searchValue={state.searchValue}
      />
      {modalsState.viewCoupon && currentCoupon && (
        <CouponViewModal
          coupon={currentCoupon}
          closeModal={handleCloseModals("viewCoupon")}
          showModal={modalsState.viewCoupon}
          couponStatusType="client"
        />
      )}
      {modalsState.createCoupon && (
        <CouponCreateModal
          closeModal={handleCloseModals("createCoupon")}
          showModal={modalsState.createCoupon}
          refresh={() =>
            getCoupons(
              state.page,
              state.perPage,
              state.field,
              state.order,
              state.searchField,
              state.searchValue,
              state.typeFilter,
              state.statusFilter,
              state.startDate,
              state.endDate
            )
          }
          action="create"
        />
      )}
      {modalsState.editCoupon && currentCoupon && (
        <CouponCreateModal
          closeModal={handleCloseModals("editCoupon")}
          showModal={modalsState.editCoupon}
          coupon={currentCoupon}
          refresh={() =>
            getCoupons(
              state.page,
              state.perPage,
              state.field,
              state.order,
              state.searchField,
              state.searchValue,
              state.typeFilter,
              state.statusFilter,
              state.startDate,
              state.endDate
            )
          }
          action="edit"
        />
      )}
      {modalsState.deleteCoupon && currentCoupon && (
        <Dialog
          fullWidth
          open={modalsState.deleteCoupon}
          onClose={handleCloseModals("deleteCoupon")}
        >
          <DialogContent>
            <RemoveEntityModalContent
              closeModal={handleCloseModals("deleteCoupon")}
              removeUser={handleDeleteCoupon(currentCoupon?._id!)}
              entityName="coupon"
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
